import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import BackButton from "../components/BackButton";
import BottomNav from "../components/BottomNav";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { MessageSquare, Users } from "lucide-react";
import { toast } from "sonner";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default function Messages() {
  const { user, loading } = useAuth();
  const [threads, setThreads] = useState([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (loading || !user) return;
    api.get("/messages/inbox")
      .then((r) => setThreads(r.data))
      .catch(() => toast.error("Failed to load messages"))
      .finally(() => setBusy(false));
  }, [user, loading]);

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-6">
        <BackButton fallback={user?.role === "brand" ? "/brand" : "/creator"} label="Back to dashboard" />
      </div>
      <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-4 pb-24 space-y-6">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2 flex items-center gap-2">
            <MessageSquare className="h-4 w-4" /> Messages
          </div>
          <h1 className="font-display font-bold text-4xl tracking-tighter">
            Every collab conversation, <span className="nexus-gradient-text">one inbox.</span>
          </h1>
          <Link to="/groups" className="inline-flex mt-3 items-center gap-2 text-xs px-3 py-1.5 rounded-full border border-white/15 text-zinc-300 hover:text-white" data-testid="messages-groups-link"><Users className="h-3.5 w-3.5" /> Group chats</Link>
        </div>
        {busy && <div className="text-zinc-500 text-sm animate-pulse">Loading…</div>}
        {!busy && threads.length === 0 && (
          <div className="glass rounded-2xl p-10 text-center text-sm text-zinc-500">
            No conversations yet — start a collab and messages will show up here.
          </div>
        )}
        <div className="space-y-2" data-testid="messages-inbox">
          {threads.map((t) => (
            <Link
              key={t.hire_id}
              to={`/messages/${t.hire_id}`}
              className="block p-4 rounded-2xl bg-[#0C0C10] hairline hover:border-white/25 transition-colors"
              data-testid={`thread-${t.hire_id}`}
            >
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full nexus-gradient flex items-center justify-center text-white font-display font-bold shrink-0">
                  {(t.other_name || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold truncate">{t.other_name}</div>
                    <div className="text-[10px] text-zinc-500 uppercase tracking-widest shrink-0">{timeAgo(t.updated_at)}</div>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-1">
                    <div className="text-sm text-zinc-400 truncate">
                      {t.last_message?.text || `New collab · ${t.package_title}`}
                    </div>
                    {t.unread > 0 && (
                      <span className="ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full nexus-gradient text-white shrink-0" data-testid={`unread-${t.hire_id}`}>
                        {t.unread}
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{t.package_title} · {t.status.replace(/_/g, " ")}</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
      <BottomNav />
    </div>
  );
}
